import React from "react";
import axios from "axios";
import { useLocation } from "react-router-dom";
import { trackQuoteSubmit, trackPhoneClick } from "../../utils/conversions";
import { getAttribution } from "../../utils/attribution";
import GoogleReviews from "../../Reviews/js/GoogleReviews";
import "../css/LandingPage.css";
import "../css/LandingPageContent.css";

const PHONE_DISPLAY = "778-238-9933";
const PHONE_TEL = "+17782389933";
const HOURS = "Mon–Fri, 7am–6pm";
const ADDRESS = "2228 Welcher Ave, Port Coquitlam BC";

// Ad-driven copy per landing route. Google scores landing-page experience on how
// closely the page answers the search term, so the eyebrow/h1/sub deliberately
// echo the phrases people actually type ("machine moving company", "heavy
// machinery movers", "industrial machine movers") plus the region. Every claim
// here is one the rest of the site already makes (25+ years, 250+ projects,
// 40+ BC cities, licensed & insured) — nothing new is invented.
const VARIANTS = {
  "machine-moving": {
    eyebrow: "Machine Moving Company · Lower Mainland BC",
    h1: "Industrial Machine Movers in Metro Vancouver",
    sub:
      "A family-run machine moving company in Port Coquitlam, moving single machines and full production lines across the Lower Mainland. Licensed and insured.",
    bullets: [
      "Heavy machinery movers for CNC, presses, lathes, boilers & HVAC units",
      "Rigging, crane and truck work handled by one crew — not three vendors",
      "Written, fixed-scope pricing before anything moves",
    ],
    serviceType: "Industrial Machine Moving",
    ctaHeading: "Ready to move your machine?",
    docTitle:
      "Machine Movers Metro Vancouver | Industrial Machine Moving — Alpha 1",
    metaDescription:
      "Industrial machine movers serving Vancouver, Surrey, Burnaby, Richmond, Langley, Delta & Port Coquitlam. Heavy machinery moving, rigging and installation. Licensed & insured, 5.0 on Google. Call 778-238-9933.",
  },
  millwright: {
    eyebrow: "Industrial Millwright Services · Lower Mainland BC",
    h1: "Millwright Installation, Alignment & Machine Relocation",
    sub:
      "Ticketed millwrights installing, levelling and laser-aligning production machinery across Metro Vancouver. Mechanical and electrical in house.",
    bullets: [
      "Machine installation, levelling & laser alignment",
      "Millwright crews with 25+ years across 40+ BC cities",
      "We work around your production to keep downtime short",
    ],
    serviceType: "Millwright Services",
    ctaHeading: "Need a millwright on site?",
    docTitle:
      "Industrial Millwright Services Metro Vancouver | Alpha 1 Industrial",
    metaDescription:
      "Millwright installation, levelling, laser alignment and machinery relocation across Metro Vancouver and the Lower Mainland. Licensed & insured, 25+ years. Call 778-238-9933.",
  },
  "crane-rigging": {
    eyebrow: "Industrial Rigging & Crane Services · Lower Mainland BC",
    h1: "Heavy Rigging, Crane & Boom Truck Services in Metro Vancouver",
    sub:
      "Controlled lifts for heavy and delicate equipment. Hiab, stiff boom, knuckle boom and mobile crane work across the Lower Mainland.",
    bullets: [
      "Rigging plans built around real site access and floor loading",
      "Picker & boom truck fleet for tight or awkward sites",
      "Licensed and fully insured — 250+ projects completed",
    ],
    serviceType: "Truck & Crane Services",
    ctaHeading: "Have a lift to plan?",
    docTitle: "Crane, Boom Truck & Industrial Rigging Metro Vancouver | Alpha 1",
    metaDescription:
      "Industrial rigging, crane and boom truck services across Metro Vancouver and the Lower Mainland. Licensed & insured heavy lift crews. Call 778-238-9933 for a fast quote.",
  },
  "plant-relocation": {
    eyebrow: "Plant & Equipment Relocation · Lower Mainland BC",
    h1: "Plant & Factory Relocation Across the Lower Mainland",
    sub:
      "Turnkey production line and full facility moves — decommission, transport, reinstall — project-managed so your operation restarts on schedule.",
    bullets: [
      "Decommission, move and reinstall handled end to end",
      "Sequenced so the line comes back up in the right order",
      "25+ years relocating industrial plants in BC",
    ],
    serviceType: "Turnkey Plant / Factory Move",
    ctaHeading: "Planning a plant move?",
    docTitle: "Plant & Factory Relocation Lower Mainland BC | Alpha 1 Industrial",
    metaDescription:
      "Turnkey plant and factory relocation across the Lower Mainland: decommission, machinery moving, transport and reinstall. Licensed & insured, 25+ years. Call 778-238-9933.",
  },
};

// Drawn from the equipment we already list on /services and in moreServices —
// these double as the phrases searchers use when they don't type "machine".
const EQUIPMENT = [
  "CNC machines & machining centres",
  "Press brakes, shears & lathes",
  "Water jet & laser cutters",
  "Milling machines",
  "Boilers, hot water tanks & HVAC units",
  "Air handling units",
  "Industrial & commercial kitchen equipment",
  "Medical, lab & dental equipment",
  "Bank safes & vaults",
  "Commercial tanks",
  "Complete production lines",
  "Fragile & delicate equipment",
];

const STEPS = [
  {
    title: "Tell us what's moving",
    text:
      "Call, or send the form with the machine, its weight or model, and the pickup and drop-off addresses. Photos of the door, floor and route help us quote accurately the first time.",
  },
  {
    title: "Site check & move plan",
    text:
      "We confirm access at both ends, what the lift needs — rigging gear, picker, boom truck or crane — and book the work around your production schedule.",
  },
  {
    title: "Fixed-scope quote in writing",
    text:
      "Scope, crew, equipment and price, in writing before anything moves. No hourly surprises at the end of the job.",
  },
  {
    title: "Moved, set and levelled",
    text:
      "Our licensed, insured crew rigs, transports and sets the machine in place. Millwright work is in house, so levelling and alignment are done before we leave.",
  },
];

// The service area named in the ads. Anything beyond this is covered by the
// "40+ cities across BC" claim used elsewhere on the site.
const SERVICE_AREA = [
  "Port Coquitlam",
  "Vancouver",
  "Burnaby",
  "Surrey",
  "Richmond",
  "Langley",
  "Delta",
];

const FAQ = [
  {
    q: "What does it cost to move a machine?",
    a:
      "Jobs are quoted on scope rather than by the hour: the weight and dimensions of the machine, access at both ends, the rigging or crane time it needs, the distance, and when the work has to happen. Send dimensions or a photo and you get a fixed-scope quote in writing.",
  },
  {
    q: "Are you licensed and insured?",
    a:
      "Yes — Alpha 1 is a licensed, fully insured industrial contractor. If your building manager or landlord needs proof of coverage before we are on site, ask and we will arrange it.",
  },
  {
    q: "How quickly can you get to us?",
    a:
      "Quote requests sent from this page are usually answered within one business day, and you can reach us directly at " +
      PHONE_DISPLAY +
      " " +
      HOURS +
      ". Moves are scheduled around your production so the line is down for as little time as possible.",
  },
  {
    q: "Do you move a single machine or a whole plant?",
    a:
      "Both. We unload single machines off containers and trucks, and we run turnkey plant and factory relocations — decommission, transport and reinstall — for entire production lines.",
  },
  {
    q: "Do you install and align the machine after the move?",
    a:
      "Yes. Millwright work is in house: installation, levelling and laser alignment, plus the mechanical and electrical side, so you are not coordinating a second trade to finish the job.",
  },
  {
    q: "Which areas do you serve?",
    a:
      "The Lower Mainland is our daily footprint — Vancouver, Burnaby, Surrey, Richmond, Langley, Delta and Port Coquitlam, where our yard is. We have worked in 40+ cities across BC and travel further for larger projects.",
  },
];

const INITIAL_FORM = {
  name: "",
  companyName: "",
  phoneNumber: "",
  email: "",
  details: "",
};

// Icons are drawn inline for the same reason GoogleReviews draws the Google "G"
// inline: zero extra network requests, no icon font, nothing to layout-shift.
const PhoneIcon = () => (
  <svg className="lp-icon" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
    <path
      fill="currentColor"
      d="M6.6 10.8a15.1 15.1 0 0 0 6.6 6.6l2.2-2.2c.3-.3.7-.4 1.1-.2 1.2.4 2.4.6 3.7.6.6 0 1 .4 1 1V20c0 .6-.4 1-1 1C10.6 21 3 13.4 3 4c0-.6.4-1 1-1h3.5c.6 0 1 .4 1 1 0 1.3.2 2.5.6 3.6.1.4 0 .8-.2 1.1l-2.3 2.1z"
    />
  </svg>
);

const StarIcon = () => (
  <svg className="lp-star" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
    <path
      fill="currentColor"
      d="M12 17.27 18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z"
    />
  </svg>
);

// The variant comes from the router rather than window.location so the same
// code runs in both places this page is rendered: at build time, where
// scripts/prerender.js renders it inside a StaticRouter and there is no
// window, and in the browser, where BrowserRouter supplies the same pathname.
// If these two ever disagreed, hydration would fail and React would throw the
// pre-built HTML away.
function variantKeyFor(pathname) {
  const seg = (pathname || "").split("/").filter(Boolean).pop();
  return VARIANTS[seg] ? seg : "machine-moving";
}

function LandingPage() {
  const { pathname } = useLocation();
  const variantKey = variantKeyFor(pathname);
  const v = VARIANTS[variantKey];

  const [formData, setFormData] = React.useState(INITIAL_FORM);
  const [status, setStatus] = React.useState({ state: "idle", message: "" });

  // This route is client-rendered, so the document title and meta description
  // still say "homepage" when an ad click lands here. Both are part of what the
  // visitor (and the quality score) sees, so swap them per variant and restore
  // them if the user navigates back into the main site.
  React.useEffect(() => {
    const previousTitle = document.title;
    const meta = document.querySelector('meta[name="description"]');
    const previousDescription = meta ? meta.getAttribute("content") : null;

    document.title = v.docTitle;
    if (meta) meta.setAttribute("content", v.metaDescription);

    return () => {
      document.title = previousTitle;
      if (meta && previousDescription !== null) {
        meta.setAttribute("content", previousDescription);
      }
    };
  }, [v.docTitle, v.metaDescription]);

  const update = (field) => (e) =>
    setFormData((prev) => ({ ...prev, [field]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus({ state: "sending", message: "" });

    // These keys are the contract /api/sendmail reads — and `name` is one of the
    // fields it hard-requires (it 400s without it), which is why the form asks
    // for it. Keep the payload in sync with that handler.
    try {
      const { status: httpStatus } = await axios.post("/api/sendmail", {
        name: formData.name.trim(),
        companyName: formData.companyName.trim(),
        email: formData.email.trim(),
        phoneNumber: formData.phoneNumber.trim(),
        serviceType: v.serviceType,
        howDidYouHear: "Google Ads - " + variantKey,
        details: formData.details.trim(),
        // Where this enquiry came from (the ad click), for the admin's
        // Leads & Sales report. Read at submit time, never during render.
        page: window.location.pathname,
        attribution: getAttribution(),
      });
      if (httpStatus === 200) {
        trackQuoteSubmit();
        setFormData(INITIAL_FORM);
        setStatus({
          state: "success",
          message:
            "Request sent. We'll get back to you shortly — usually within one business day.",
        });
      } else {
        setStatus({
          state: "error",
          message:
            "Something went wrong on our end. Please call " + PHONE_DISPLAY + ".",
        });
      }
    } catch (err) {
      setStatus({
        state: "error",
        message:
          "We couldn't send that. Please try again, or call " +
          PHONE_DISPLAY +
          ".",
      });
    }
  };

  const sending = status.state === "sending";

  return (
    <div className="lp">
      <a className="lp-skip" href="#quote">
        Skip to the quote form
      </a>

      <header className="lp-bar">
        <a
          href="/"
          className="lp-logo-link"
          aria-label="Alpha 1 Industrial Services home"
        >
          <img
            src="/images/Logos/SmallLogo.png"
            alt="Alpha 1 Industrial Services"
            className="lp-logo"
            width="150"
            height="38"
          />
        </a>
        <div className="lp-bar-right">
          <span className="lp-bar-hours">{HOURS}</span>
          <a
            className="lp-bar-call"
            href={`tel:${PHONE_TEL}`}
            onClick={trackPhoneClick}
          >
            <PhoneIcon />
            {PHONE_DISPLAY}
          </a>
        </div>
      </header>

      <main>
        <section className="lp-hero" aria-labelledby="lp-h1">
          <div className="lp-hero-inner">
            <div className="lp-hero-copy">
              <span className="lp-eyebrow">{v.eyebrow}</span>
              <h1 className="lp-h1" id="lp-h1">
                {v.h1}
              </h1>
              <p className="lp-sub">{v.sub}</p>

              <div className="lp-cta-row">
                <a
                  className="lp-call-btn"
                  href={`tel:${PHONE_TEL}`}
                  onClick={trackPhoneClick}
                >
                  <PhoneIcon />
                  Call {PHONE_DISPLAY}
                </a>
                <a className="lp-quote-btn" href="#quote">
                  Get a free quote
                </a>
              </div>
              <p className="lp-cta-note">
                Answered {HOURS} · Quotes usually back within one business day
              </p>

              <ul className="lp-proof">
                <li className="lp-proof-item lp-proof-rating">
                  <StarIcon />
                  <strong>5.0</strong> Google rating
                </li>
                <li className="lp-proof-item">Licensed &amp; fully insured</li>
                <li className="lp-proof-item">25+ years · 250+ projects</li>
                <li className="lp-proof-item">
                  Port Coquitlam yard · Lower Mainland daily
                </li>
              </ul>
            </div>

            <form
              id="quote"
              className="lp-form"
              onSubmit={handleSubmit}
              aria-labelledby="lp-form-title"
            >
              <h2 className="lp-form-title" id="lp-form-title">
                Get a fast, free quote
              </h2>
              <p className="lp-form-lead">
                Tell us what needs moving. Most requests are answered within one
                business day.
              </p>

              <div className="lp-field">
                <label htmlFor="lp-name">
                  Your name <span aria-hidden="true">*</span>
                </label>
                <input
                  id="lp-name"
                  type="text"
                  autoComplete="name"
                  required
                  placeholder="Jane Smith"
                  value={formData.name}
                  onChange={update("name")}
                />
              </div>

              <div className="lp-field">
                <label htmlFor="lp-company">
                  Company <span aria-hidden="true">*</span>
                </label>
                <input
                  id="lp-company"
                  type="text"
                  autoComplete="organization"
                  required
                  placeholder="Acme Manufacturing"
                  value={formData.companyName}
                  onChange={update("companyName")}
                />
              </div>

              <div className="lp-field">
                <label htmlFor="lp-phone">
                  Phone <span aria-hidden="true">*</span>
                </label>
                <input
                  id="lp-phone"
                  type="tel"
                  inputMode="tel"
                  autoComplete="tel"
                  required
                  placeholder="(778) 555-0123"
                  value={formData.phoneNumber}
                  onChange={update("phoneNumber")}
                />
              </div>

              <div className="lp-field">
                <label htmlFor="lp-email">
                  Email <span aria-hidden="true">*</span>
                </label>
                <input
                  id="lp-email"
                  type="email"
                  autoComplete="email"
                  required
                  placeholder="you@company.com"
                  value={formData.email}
                  onChange={update("email")}
                />
              </div>

              <div className="lp-field">
                <label htmlFor="lp-details">
                  What needs to be moved? <span aria-hidden="true">*</span>
                </label>
                <textarea
                  id="lp-details"
                  rows="3"
                  required
                  placeholder="e.g. one 6,000 lb CNC mill, Surrey to Langley, ground level both ends, needed in 3 weeks."
                  value={formData.details}
                  onChange={update("details")}
                />
              </div>

              <button type="submit" className="lp-submit" disabled={sending}>
                {sending ? "Sending…" : "Request my free quote"}
              </button>

              <p className="lp-msg" role="status" aria-live="polite">
                {status.message && (
                  <span className={`lp-msg-text lp-msg-text--${status.state}`}>
                    {status.message}
                  </span>
                )}
              </p>

              <p className="lp-form-note">
                No obligation — your details are used only to quote this job. Or
                call{" "}
                <a href={`tel:${PHONE_TEL}`} onClick={trackPhoneClick}>
                  {PHONE_DISPLAY}
                </a>{" "}
                · {HOURS}
              </p>
            </form>

            <ul className="lp-bullets">
              {v.bullets.map((b) => (
                <li key={b}>{b}</li>
              ))}
            </ul>
          </div>
        </section>

        <section className="lp-trust" aria-label="Alpha 1 by the numbers">
          <div className="lp-trust-item">
            <strong>25+</strong>
            <span>Years experience</span>
          </div>
          <div className="lp-trust-item">
            <strong>250+</strong>
            <span>Projects completed</span>
          </div>
          <div className="lp-trust-item">
            <strong>40+</strong>
            <span>BC cities served</span>
          </div>
          <div className="lp-trust-item">
            <strong>5.0</strong>
            <span>Rating on Google</span>
          </div>
          <div className="lp-trust-item">
            <strong>Licensed</strong>
            <span>&amp; fully insured</span>
          </div>
        </section>

        <section className="lp-sec" aria-labelledby="lp-moves-h">
          <div className="lp-sec-inner">
            <span className="lp-kicker">Equipment we move</span>
            <h2 className="lp-h2" id="lp-moves-h">
              Heavy machinery movers for almost anything on a shop floor
            </h2>
            <p className="lp-sec-lead">
              If it is bolted down, oversized or too heavy for a regular moving
              company, it is the kind of work we do every week across the Lower
              Mainland.
            </p>
            <ul className="lp-tiles">
              {EQUIPMENT.map((item) => (
                <li className="lp-tile" key={item}>
                  {item}
                </li>
              ))}
            </ul>
          </div>
        </section>

        <section className="lp-sec lp-sec--dark" aria-labelledby="lp-steps-h">
          <div className="lp-sec-inner">
            <span className="lp-kicker">How it works</span>
            <h2 className="lp-h2" id="lp-steps-h">
              From first call to machine levelled in place
            </h2>
            <ol className="lp-steps">
              {STEPS.map((step, i) => (
                <li className="lp-step" key={step.title}>
                  <span className="lp-step-num" aria-hidden="true">
                    {i + 1}
                  </span>
                  <h3 className="lp-step-title">{step.title}</h3>
                  <p className="lp-step-text">{step.text}</p>
                </li>
              ))}
            </ol>
          </div>
        </section>

        <section className="lp-sec" aria-labelledby="lp-area-h">
          <div className="lp-sec-inner lp-area">
            <div className="lp-area-copy">
              <span className="lp-kicker">Service area</span>
              <h2 className="lp-h2" id="lp-area-h">
                Machine movers serving Metro Vancouver &amp; the Lower Mainland
              </h2>
              <p className="lp-sec-lead">
                Our yard is at {ADDRESS}, so most of the Lower Mainland is a
                short run for our trucks. We have worked in 40+ cities across BC
                and travel further for larger projects.
              </p>
              <ul className="lp-chips">
                {SERVICE_AREA.map((city) => (
                  <li className="lp-chip" key={city}>
                    {city}
                  </li>
                ))}
                <li className="lp-chip lp-chip--muted">+ 40 more BC cities</li>
              </ul>
            </div>
            <div className="lp-area-card">
              <h3 className="lp-area-card-title">Talk to a mover now</h3>
              <a
                className="lp-call-btn lp-call-btn--block"
                href={`tel:${PHONE_TEL}`}
                onClick={trackPhoneClick}
              >
                <PhoneIcon />
                Call {PHONE_DISPLAY}
              </a>
              <dl className="lp-facts">
                <div className="lp-fact">
                  <dt>Hours</dt>
                  <dd>{HOURS}</dd>
                </div>
                <div className="lp-fact">
                  <dt>Yard</dt>
                  <dd>{ADDRESS}</dd>
                </div>
                <div className="lp-fact">
                  <dt>Quotes</dt>
                  <dd>Usually within one business day</dd>
                </div>
              </dl>
            </div>
          </div>
        </section>

        <GoogleReviews />

        <section className="lp-sec" aria-labelledby="lp-faq-h">
          <div className="lp-sec-inner lp-sec-inner--narrow">
            <span className="lp-kicker">Straight answers</span>
            <h2 className="lp-h2" id="lp-faq-h">
              Questions we get before a move
            </h2>
            <div className="lp-faq">
              {FAQ.map((item) => (
                <details className="lp-faq-item" key={item.q}>
                  <summary className="lp-faq-q">{item.q}</summary>
                  <p className="lp-faq-a">{item.a}</p>
                </details>
              ))}
            </div>
          </div>
        </section>

        <section className="lp-final" aria-labelledby="lp-final-h">
          <h2 className="lp-final-h" id="lp-final-h">
            {v.ctaHeading}
          </h2>
          <p className="lp-final-sub">
            Call and we will scope it on the phone, or send the form and we will
            come back with a written quote.
          </p>
          <div className="lp-cta-row lp-cta-row--center">
            <a
              className="lp-call-btn"
              href={`tel:${PHONE_TEL}`}
              onClick={trackPhoneClick}
            >
              <PhoneIcon />
              Call {PHONE_DISPLAY}
            </a>
            <a className="lp-quote-btn" href="#quote">
              Request a quote
            </a>
          </div>
        </section>
      </main>

      <footer className="lp-foot">
        <nav className="lp-foot-nav" aria-label="Alpha 1 website">
          <a href="/">Full website</a>
          <a href="/services">Services</a>
          <a href="/portfolio">Past projects</a>
          <a href="/contact">Contact</a>
        </nav>
        <span className="lp-foot-meta">
          Alpha 1 Industrial Services · {ADDRESS} ·{" "}
          <a href={`tel:${PHONE_TEL}`} onClick={trackPhoneClick}>
            {PHONE_DISPLAY}
          </a>{" "}
          · {HOURS}
        </span>
      </footer>

      {/* Mobile-only fixed action bar: on a phone the two things a visitor can
          do must always be one thumb-tap away, which is exactly what Google
          checks for on mobile. Hidden on desktop, where the sticky header and
          the hero form are already in view. */}
      <div className="lp-actionbar">
        <a
          className="lp-actionbar-call"
          href={`tel:${PHONE_TEL}`}
          onClick={trackPhoneClick}
        >
          <PhoneIcon />
          Call now
        </a>
        <a className="lp-actionbar-quote" href="#quote">
          Free quote
        </a>
      </div>
    </div>
  );
}

export default LandingPage;

// Used by scripts/prerender.js for each variant's <title> and description.
export { VARIANTS };
